import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { notificationService } from '@/services/api';
import { APP_NAME } from '@/constants';

const ACTION_LABEL: Record<string, string> = {
  like: 'liked your post',
  comment: 'commented on your post',
  reply: 'replied to your comment',
  repost: 'reposted your post',
  follow: 'started following you',
  message: 'sent you a message',
};

interface NotificationContextValue {
  unreadCount: number;
  /** Most recent notification, enriched with the actor's profile. */
  latest: any | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [unreadCount, setUnreadCount] = useState(0);
  const [latest, setLatest] = useState<any | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      return;
    }
    const { data } = await notificationService.getUnreadCount();
    setUnreadCount(data || 0);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const markRead = useCallback(async (id: string) => {
    await notificationService.markAsRead(id);
    setUnreadCount((count) => Math.max(0, count - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await notificationService.markAllAsRead();
    setUnreadCount(0);
  }, []);

  // One subscription for the whole session, independent of the current page.
  useEffect(() => {
    if (!user?.id) {
      setUnreadCount(0);
      setLatest(null);
      return;
    }

    const channel = supabase.channel(`notifications-${user.id}`);
    channel
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload: any) => {
          const row = payload.new;
          setUnreadCount((count) => count + 1);

          // Play notification sound.
          try {
            const audio = new Audio('/audio/notification.mp3');
            audio.volume = 0.7;
            void audio.play().catch(() => {
              // Autoplay blocked or file missing; silent fallback.
            });
          } catch {
            // Audio not supported.
          }

          // The realtime payload has no joined actor, so fetch it for the toast
          // and for the notification list item.
          void (async () => {
            const { data: actor } = await (supabase as any)
              .from('profiles')
              .select('*')
              .eq('id', row.actor_id)
              .maybeSingle();

            setLatest({ ...row, profiles: actor ?? null });

            const name = actor?.display_name || actor?.username;
            const action = ACTION_LABEL[row.type] || 'sent you a notification';
            toast('New notification', name ? `${name} ${action}` : action);
          })();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [toast, user?.id]);

  // Surface the unread count in the tab title.
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount > 99 ? '99+' : unreadCount}) ${APP_NAME}`;
    } else {
      document.title = APP_NAME;
    }
    return () => {
      document.title = APP_NAME;
    };
  }, [unreadCount]);

  return (
    <NotificationContext.Provider
      value={{ unreadCount, latest, refresh, markRead, markAllRead }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within a NotificationProvider');
  return context;
}
